import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  CheckCircle, 
  User, 
  Mail, 
  Phone, 
  Ruler, 
  Shield, 
  AlertTriangle,
  Loader2,
  ArrowRight,
  Eye,
  EyeOff,
  LogIn,
  UserPlus
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { API_BASE } from '@/config/api';
import CommonHeader from '@/components/CommonHeader';
import { EnhancedHeader } from '@/components/common/EnhancedHeader';
import Logo from '@/components/Logo';

interface LinkingData {
  tailor_name: string;
  tailor_email: string;
  expires_at: string;
  is_valid: boolean;
}

interface CustomerData {
  name: string;
  email: string;
  phone: string;
  measurements: any[];
  preferences: any;
}

const CustomerLinking: React.FC = () => {
  const { linkToken } = useParams<{ linkToken: string }>();
  const navigate = useNavigate();
  const { user, token } = useAuth();
  
  const [linkingData, setLinkingData] = useState<LinkingData | null>(null);
  const [customerData, setCustomerData] = useState<CustomerData | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  
  // Privacy controls
  const [shareBasicInfo, setShareBasicInfo] = useState(true);
  const [shareMeasurements, setShareMeasurements] = useState(true);
  const [sharePreferences, setSharePreferences] = useState(true);
  const [showMeasurements, setShowMeasurements] = useState(false);

  useEffect(() => {
    if (linkToken) {
      validateLink();
    }
  }, [linkToken]);

  const validateLink = async () => {
    try {
      console.log('Validating link with token:', linkToken);
      console.log('API_BASE:', API_BASE);
      
      const response = await fetch(`${API_BASE}/api/customer/link/validate/${linkToken}`);
      console.log('Validation response status:', response.status);
      
      if (response.ok) {
        const data = await response.json();
        console.log('Validation data:', data);
        setLinkingData(data);
        
        // If user is logged in, fetch their data
        if (token) {
          await fetchCustomerData();
        }
      } else {
        const errorText = await response.text();
        console.log('Validation error response:', errorText);
        setError('Invalid or expired link. Please request a new one from your tailor.');
      }
    } catch (_err) {
      console.error('Validation error:', err);
      setError('Failed to validate link. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fetchCustomerData = async () => {
    try {
      console.log('Fetching customer data with token:', token);
      const response = await fetch(`${API_BASE}/api/customers/profile`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      console.log('Customer data response status:', response.status);
      if (response.ok) {
        const data = await response.json();
        console.log('Customer data received:', data);
        setCustomerData(data);
      } else if (response.status === 404) {
        // Customer profile doesn't exist - this is a new customer
        console.log('Customer profile not found - new customer');
        setCustomerData(null);
      } else {
        const errorText = await response.text();
        console.log('Customer data error response:', errorText);
        setCustomerData(null);
      }
    } catch (_err) {
      console.error('Failed to fetch customer data:', err);
      setCustomerData(null);
    }
  };

  const handleLinkWithTailor = async () => {
    if (!linkingData) return;
    
    if (!customerData) {
      console.log('Cannot link: customerData is missing');
      setError('Please wait for your profile to load, or try refreshing the page.');
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      const response = await fetch(`${API_BASE}/api/customer/link/complete/${linkToken}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          share_basic_info: shareBasicInfo,
          share_measurements: shareMeasurements,
          share_preferences: sharePreferences,
          customer_data: customerData
        })
      });

      if (response.ok) {
        setSuccess(true);
        setTimeout(() => {
          navigate('/dashboard');
        }, 3000);
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Failed to complete linking');
      }
    } catch (_err) {
      setError('Network error occurred. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleLogin = () => {
    navigate('/login', { state: { returnTo: `/customer/link/${linkToken}` } });
  };

  const handleSignup = () => {
    navigate('/signup', { state: { returnTo: `/customer/link/${linkToken}` } });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        {token ? (
          <EnhancedHeader title="Customer Linking" showBackButton={true} backTo="/" />
        ) : (
          <CommonHeader title="Customer Linking" showBackButton={true} backTo="/" />
        )}
        <div className="flex items-center justify-center min-h-[calc(100vh-80px)]">
          <div className="text-center">
            <Logo size="lg" className="mx-auto mb-4" />
            <div className="text-gray-600">Validating link...</div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-white">
        {token ? (
          <EnhancedHeader title="Customer Linking" showBackButton={true} backTo="/" />
        ) : (
          <CommonHeader title="Customer Linking" showBackButton={true} backTo="/" />
        )}
        <div className="flex items-center justify-center min-h-[calc(100vh-80px)]">
          <div className="text-center max-w-md mx-auto px-4">
            <Logo size="lg" className="mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Link Not Valid</h1>
            <p className="text-gray-600 mb-6">{error}</p>
            <button
              onClick={() => navigate('/')}
              className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
            >
              Go to Homepage
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-white">
        {token ? (
          <EnhancedHeader title="Customer Linking" showBackButton={true} backTo="/" />
        ) : (
          <CommonHeader title="Customer Linking" showBackButton={true} backTo="/" />
        )}
        <div className="flex items-center justify-center min-h-[calc(100vh-80px)]">
          <div className="text-center max-w-md mx-auto px-4">
            <Logo size="lg" className="mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Successfully Connected!</h1>
            <p className="text-gray-600 mb-6">You are now connected with {linkingData?.tailor_name}</p>
            <div className="text-sm text-gray-500">Redirecting to your dashboard...</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Standard Header - Use EnhancedHeader when logged in, CommonHeader when not */}
      {token ? (
        <EnhancedHeader 
          title="Customer Linking" 
          showBackButton={true} 
          backTo="/"
        />
      ) : (
        <CommonHeader 
          title="Customer Linking" 
          showBackButton={true} 
          backTo="/"
        />
      )}

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        {!token && linkingData && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden"
          >
            {/* Header Section with Gradient Background */}
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 px-6 sm:px-8 py-8 sm:py-10 text-center border-b border-gray-200">
              <Logo size="lg" className="mx-auto mb-4" />
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-3">Connect with Your Tailor</h1>
              <div className="inline-flex items-center justify-center px-4 py-2 bg-white rounded-full shadow-sm border border-blue-100 mb-3">
                <User className="w-4 h-4 text-blue-600 mr-2" />
                <p className="text-base sm:text-lg text-gray-700">
                  <span className="font-semibold text-blue-600">{linkingData.tailor_name}</span> wants to connect with you
                </p>
              </div>
              <p className="text-sm sm:text-base text-gray-600 max-w-2xl mx-auto">
                Get personalized tailoring services and manage your measurements securely
              </p>
            </div>

            {/* Action Required Section */}
            <div className="px-6 sm:px-8 py-6 sm:py-8">
              <div className="bg-blue-50 border-l-4 border-blue-500 rounded-r-lg p-6 mb-6">
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <AlertTriangle className="w-6 h-6 text-blue-600" />
                  </div>
                  <div className="ml-4 flex-1">
                    <h2 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2">Ready to Connect?</h2>
                    <p className="text-sm sm:text-base text-gray-700 mb-4">
                      To connect with your tailor and share your information securely, please sign in to your account or create a new one.
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-4 mb-6">
                <button
                  onClick={handleLogin}
                  className="flex-1 flex items-center justify-center bg-blue-600 text-white px-6 py-3.5 rounded-lg hover:bg-blue-700 active:bg-blue-800 transition-all duration-200 font-semibold shadow-md hover:shadow-lg transform hover:-translate-y-0.5"
                >
                  <LogIn className="w-5 h-5 mr-2" />
                  <span className="text-sm sm:text-base">Sign In</span>
                </button>
                <button
                  onClick={handleSignup}
                  className="flex-1 flex items-center justify-center bg-white text-blue-600 border-2 border-blue-600 px-6 py-3.5 rounded-lg hover:bg-blue-50 active:bg-blue-100 transition-all duration-200 font-semibold shadow-sm hover:shadow-md"
                >
                  <UserPlus className="w-5 h-5 mr-2" />
                  <span className="text-sm sm:text-base">Create Account</span>
                </button>
              </div>

              {/* Security Notice */}
              <div className="flex items-center justify-center text-xs sm:text-sm text-gray-500 bg-gray-50 rounded-lg py-3 px-4">
                <Shield className="w-4 h-4 mr-2 text-green-600" />
                <span>Your data is encrypted and protected with enterprise-grade security</span>
              </div>
            </div>
          </motion.div>
        )}

        {/* Show content when user IS logged in */}
        {token && linkingData && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className="space-y-6 sm:space-y-8"
          >
            {/* Connection Header */}
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl shadow-lg border border-gray-200 p-6 sm:p-8 text-center overflow-hidden">
              <Logo size="lg" className="mx-auto mb-4" />
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-3">Connect with Your Tailor</h1>
              <div className="inline-flex items-center justify-center px-4 py-2 bg-white rounded-full shadow-sm border border-blue-100">
                <User className="w-4 h-4 text-blue-600 mr-2" />
                <p className="text-base sm:text-lg text-gray-700">
                  <span className="font-semibold text-blue-600">{linkingData.tailor_name}</span> wants to connect with you
                </p>
              </div>
            </div>

            {/* Existing Customer Flow - Show Privacy Controls */}
            {customerData && (
              <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6 sm:p-8">
                <div className="mb-6">
                  <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">Privacy Controls</h2>
                  <p className="text-sm sm:text-base text-gray-600">Choose what information you'd like to share with your tailor. You can change these settings anytime.</p>
                </div>
                
                <div className="space-y-4 sm:space-y-6">
                  {/* Basic Information */}
                  <div className="border-2 border-gray-200 rounded-xl p-5 sm:p-6 hover:border-blue-300 transition-colors bg-gray-50/50">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center flex-1 min-w-0">
                        <div className="flex-shrink-0 w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mr-3">
                          <User className="w-5 h-5 text-blue-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold text-gray-900 text-base sm:text-lg">Basic Information</h3>
                          <p className="text-xs sm:text-sm text-gray-600 truncate">Name, email, and phone number</p>
                        </div>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer flex-shrink-0 ml-4">
                        <input
                          type="checkbox"
                          checked={shareBasicInfo}
                          onChange={(e) => setShareBasicInfo(e.target.checked)}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                      </label>
                    </div>
                    {shareBasicInfo && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        className="mt-4 pt-4 border-t border-gray-200"
                      >
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
                          <div className="flex items-center text-sm sm:text-base text-gray-700 bg-white rounded-lg p-3">
                            <User className="w-4 h-4 mr-2 text-blue-600 flex-shrink-0" />
                            <span className="truncate">{customerData.name || 'Unknown'}</span>
                          </div>
                          <div className="flex items-center text-sm sm:text-base text-gray-700 bg-white rounded-lg p-3">
                            <Mail className="w-4 h-4 mr-2 text-blue-600 flex-shrink-0" />
                            <span className="truncate">{customerData.email || 'Unknown'}</span>
                          </div>
                          <div className="flex items-center text-sm sm:text-base text-gray-700 bg-white rounded-lg p-3">
                            <Phone className="w-4 h-4 mr-2 text-blue-600 flex-shrink-0" />
                            <span className="truncate">{customerData.phone || 'Unknown'}</span>
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </div>

                  {/* Measurements */}
                  <div className="border-2 border-gray-200 rounded-xl p-5 sm:p-6 hover:border-green-300 transition-colors bg-gray-50/50">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center flex-1 min-w-0">
                        <div className="flex-shrink-0 w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center mr-3">
                          <Ruler className="w-5 h-5 text-green-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold text-gray-900 text-base sm:text-lg">Measurements</h3>
                          <p className="text-xs sm:text-sm text-gray-600 truncate">Your body measurements for accurate tailoring</p>
                        </div>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer flex-shrink-0 ml-4">
                        <input
                          type="checkbox"
                          checked={shareMeasurements}
                          onChange={(e) => setShareMeasurements(e.target.checked)}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-green-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-600"></div>
                      </label>
                    </div>
                    {shareMeasurements && customerData.measurements && customerData.measurements.length > 0 && (
                      <div className="mt-4">
                        <button
                          onClick={() => setShowMeasurements(!showMeasurements)}
                          className="flex items-center text-sm text-green-600 hover:text-green-700"
                        >
                          {showMeasurements ? <EyeOff className="w-4 h-4 mr-1" /> : <Eye className="w-4 h-4 mr-1" />}
                          {showMeasurements ? 'Hide' : 'View'} Measurements
                        </button>
                        {showMeasurements && (
                          <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                            {customerData.measurements.map((measurement: any, index: number) => (
                              <div key={index} className="bg-green-50 p-3 rounded">
                                <div className="font-medium text-gray-900">{measurement.name}</div>
                                <div className="text-green-600">{measurement.value}"</div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                    {shareMeasurements && (!customerData.measurements || customerData.measurements.length === 0) && (
                      <div className="text-sm text-gray-500 italic">
                        No measurements found. You can add them later in your profile.
                      </div>
                    )}
                  </div>

                  {/* Preferences */}
                  <div className="border-2 border-gray-200 rounded-xl p-5 sm:p-6 hover:border-purple-300 transition-colors bg-gray-50/50">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center flex-1 min-w-0">
                        <div className="flex-shrink-0 w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center mr-3">
                          <Shield className="w-5 h-5 text-purple-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold text-gray-900 text-base sm:text-lg">Preferences</h3>
                          <p className="text-xs sm:text-sm text-gray-600 truncate">Size preferences and style choices</p>
                        </div>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer flex-shrink-0 ml-4">
                        <input
                          type="checkbox"
                          checked={sharePreferences}
                          onChange={(e) => setSharePreferences(e.target.checked)}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-purple-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600"></div>
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* New Customer Flow - Show Registration Message */}
            {!customerData && (
              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-xl shadow-lg p-6 sm:p-8">
                <div className="text-center mb-6">
                  <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-3">Welcome to Master Ji!</h2>
                  <p className="text-base sm:text-lg text-gray-700 max-w-2xl mx-auto">
                    It looks like you don't have a customer profile yet. To connect with <span className="font-semibold text-blue-600">{linkingData.tailor_name}</span>, 
                    you'll need to complete your profile setup first.
                  </p>
                </div>
                <div className="bg-white border-2 border-blue-200 rounded-xl p-6 mb-6 shadow-sm">
                  <h3 className="font-semibold text-gray-900 mb-4 text-lg">What happens next?</h3>
                  <ul className="space-y-3">
                    <li className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                      <span className="text-sm sm:text-base text-gray-700">Complete your profile with basic information</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                      <span className="text-sm sm:text-base text-gray-700">Add your body measurements for accurate tailoring</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                      <span className="text-sm sm:text-base text-gray-700">Connect with your tailor and start ordering</span>
                    </li>
                  </ul>
                </div>
                <div className="flex flex-col sm:flex-row gap-4">
                  <button
                    onClick={() => navigate('/signup', { state: { returnTo: `/customer/link/${linkToken}` } })}
                    className="flex-1 flex items-center justify-center bg-blue-600 text-white border-2 border-blue-600 px-6 py-3.5 rounded-lg hover:bg-blue-700 active:bg-blue-800 transition-all duration-200 font-semibold shadow-md hover:shadow-lg transform hover:-translate-y-0.5"
                  >
                    <UserPlus className="w-5 h-5 mr-2" />
                    <span>Complete Profile Setup</span>
                  </button>
                  <button
                    onClick={() => navigate('/')}
                    className="flex-1 sm:flex-initial flex items-center justify-center bg-white text-gray-700 border-2 border-gray-300 px-6 py-3.5 rounded-lg hover:bg-gray-50 active:bg-gray-100 transition-all duration-200 font-medium"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {/* Action Buttons - Only show for existing customers */}
            {customerData && (
              <div className="bg-gradient-to-br from-gray-50 to-blue-50 rounded-xl shadow-lg border border-gray-200 p-6 sm:p-8">
                <div className="flex flex-col sm:flex-row gap-4 justify-between items-stretch sm:items-center">
                  <button
                    onClick={() => navigate('/dashboard')}
                    className="px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-white hover:border-gray-400 active:bg-gray-50 transition-all duration-200 font-medium"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleLinkWithTailor}
                    disabled={submitting}
                    className="flex items-center justify-center bg-blue-600 text-white px-8 py-3.5 rounded-lg hover:bg-blue-700 active:bg-blue-800 transition-all duration-200 font-semibold shadow-md hover:shadow-lg transform hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                        <span>Connecting...</span>
                      </>
                    ) : (
                      <>
                        <span>Connect with Tailor</span>
                        <ArrowRight className="w-5 h-5 ml-2" />
                      </>
                    )}
                  </button>
                </div>
                <div className="mt-6 flex items-center justify-center text-xs sm:text-sm text-gray-500 bg-white/60 rounded-lg py-3 px-4">
                  <Shield className="w-4 h-4 mr-2 text-green-600" />
                  <span>Your data is encrypted and protected with enterprise-grade security</span>
                </div>
              </div>
            )}
          </motion.div>
        )}

        {/* Fallback content when no linkingData */}
        {!linkingData && !loading && !error && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className="bg-white rounded-xl shadow-lg border border-gray-200 p-8 sm:p-12 text-center"
          >
            <Logo size="lg" className="mx-auto mb-6" />
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-3">Welcome to Master Ji</h1>
            <p className="text-base sm:text-lg text-gray-600 mb-8 max-w-2xl mx-auto">
              Connect with your tailor to get started with personalized tailoring services
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center max-w-md mx-auto">
              <button
                onClick={handleLogin}
                className="flex-1 flex items-center justify-center bg-blue-600 text-white px-6 py-3.5 rounded-lg hover:bg-blue-700 active:bg-blue-800 transition-all duration-200 font-semibold shadow-md hover:shadow-lg transform hover:-translate-y-0.5"
              >
                <LogIn className="w-5 h-5 mr-2" />
                <span>Sign In</span>
              </button>
              <button
                onClick={handleSignup}
                className="flex-1 flex items-center justify-center bg-white text-blue-600 border-2 border-blue-600 px-6 py-3.5 rounded-lg hover:bg-blue-50 active:bg-blue-100 transition-all duration-200 font-semibold shadow-sm hover:shadow-md"
              >
                <UserPlus className="w-5 h-5 mr-2" />
                <span>Create Account</span>
              </button>
            </div>
          </motion.div>
        )}
      </main>
    </div>
  );
};

export default CustomerLinking;